import React, { createContext, useContext } from 'react';

/**
 * AiStatusContext — single source of truth for "is the AI service available?"
 *
 * Strategy: read at BUILD TIME from `REACT_APP_AI_ENABLED` in `frontend/.env`.
 * This is a zero-cost flag — no HTTP request, no useEffect, no race condition,
 * works identically across multiple backend pods. If the admin needs to toggle
 * AI off, they set `REACT_APP_AI_ENABLED=false` and redeploy the frontend
 * (the same release cadence as any other env-driven flag).
 *
 * The backend `/api/ai/status` endpoint is kept for server-side health checks
 * (and for tests), but the frontend never calls it anymore.
 *
 * Why not a runtime check?
 *   - 1 call per session per browser × N visitors = wasted budget
 *   - StrictMode in dev doubles the calls
 *   - Race conditions: button mounts before status loads → flicker
 *   - Build-time env is what CRA recommends for feature flags
 */

// `REACT_APP_AI_ENABLED` is inlined by CRA at build time. Anything other than
// the literal string "true" (case-insensitive) disables AI.
const AI_ENABLED = String(process.env.REACT_APP_AI_ENABLED || '').toLowerCase() === 'true';

const AiStatusContext = createContext({
  enabled: AI_ENABLED,
  loading: false,  // always false — no async resolution needed
  refresh: () => {},
});

export function AiStatusProvider({ children }) {
  // Stable identity — value object is constant per page load.
  const value = { enabled: AI_ENABLED, loading: false, refresh: () => {} };
  return (
    <AiStatusContext.Provider value={value}>
      {children}
    </AiStatusContext.Provider>
  );
}

export function useAiStatus() {
  return useContext(AiStatusContext);
}
